import { Link, Modal } from '@jd/platform-config';
import { DeepNullToUndefined } from '@jd/client/utils/unnulable';
import { joinUrl } from '../toUrl';
import { appendQueryString } from '../queryParams/appendQueryString';
import { buildQueryString } from '../queryParams/buildQueryString';
import { queryParamsMapper } from './queryParams';

const appearanceOptions = ['cta', 'outlined', 'contained', 'link', 'text'];

type InputType = DeepNullToUndefined<Link & { appearance?: string }>;
export type LinkMapperInputType = InputType;

export type TBLinkType = {
  label: string;
  href: string;
  newTab: boolean;
  appearance?: 'cta' | 'outlined' | 'contained' | 'link' | 'text';
};

type LinkMapperOptions = {
  blogHubSlug?: string;
};

export const hrefOnlyLinkMapper = <T extends Partial<InputType> | undefined>(
  input: T,
  options?: LinkMapperOptions,
): string | undefined => {
  if (!input) {
    // @ts-ignore
    return undefined;
  }
  const { reference, url, type, sectionId, queryParams } = input;
  const isCustom = type !== 'reference';

  const resolveSlug = () => {
    let value = reference?.value;
    let prefix = '';
    if (reference?.relationTo === 'modals') {
      prefix = '/modal';
      // @ts-ignore
      value = (value as Modal).redirectPage;
    }
    if (reference?.relationTo === 'posts') {
      prefix = joinUrl([options?.blogHubSlug || 'blog'], { local: true });
    }

    let slug;
    if (typeof value === 'string') {
      slug = `/id/${value}`;
    } else if (value && 'slug' in value) {
      slug = `${prefix}/${value.slug}`;
    }

    if (sectionId) {
      slug = `${slug}#${sectionId}`;
    }
    return slug;
  };

  const href = isCustom ? url : resolveSlug();
  if (!href) return undefined;

  return appendQueryString({
    url: href,
    queryString: buildQueryString(queryParamsMapper(queryParams)),
  });
};

const linkMapper = <T extends Partial<InputType> | undefined>(
  ...args: T extends InputType
    ? [inputLink: T, allowPartial?: boolean, options?: LinkMapperOptions]
    : [inputLink: T, allowPartial: true, options?: LinkMapperOptions]
): T extends InputType ? TBLinkType : TBLinkType | undefined => {
  const [input, allowPartial, options] = args;
  if (!input) {
    // @ts-ignore
    return undefined;
  }
  const { label, newTab, appearance } = input;
  const href = hrefOnlyLinkMapper(input, options);

  if (!label || !href) {
    if (allowPartial) {
      // @ts-ignore
      return undefined;
    } else {
      throw new Error('Link requires custom label and href');
    }
  }

  return {
    label: label,
    href,
    newTab: !!newTab,
    appearance: appearanceOptions.includes(appearance!)
      ? (appearance as TBLinkType['appearance'])
      : undefined,
  };
};

export default linkMapper;
